import { useEffect, useMemo, useState } from 'react'
import type { Site } from './types'
import { fetchTamper } from './api'
import { HEALTH_POLL_MS } from './constants'
import type { SiteTamper } from './siteIssues'

/**
 * Polls which cameras are currently tampered, for every configured site.
 *
 * Polled for the same reason health is: a camera being interfered with reaches
 * the server as an event, but whether it is *still* interfered with is a state
 * nothing re-announces. Shares health's interval because the two are read
 * together and a second cadence would only add requests.
 *
 * A failed poll keeps the last answer rather than blanking the indicator — a
 * briefly unreachable server should not make a blinded camera look fine.
 */
export function useTamper(sites: Record<string, Site>): SiteTamper {
  const [tamper, setTamper] = useState<SiteTamper>({})
  const siteIds = Object.keys(sites).sort().join(',')

  useEffect(() => {
    if (!siteIds) return
    let cancelled = false
    const ids = siteIds.split(',')

    const poll = () => {
      ids.forEach((siteId) => {
        fetchTamper(siteId)
          .then((t) => { if (!cancelled) setTamper((prev) => ({ ...prev, [siteId]: t })) })
          .catch(() => {})
      })
    }

    poll()
    const t = setInterval(poll, HEALTH_POLL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [siteIds])

  // Derived rather than stored pruned, as health is: a site can leave config
  // or have access withdrawn, and whatever it last said is stale the moment
  // it goes.
  return useMemo(() => {
    const ids = siteIds ? siteIds.split(',') : []
    const visible: SiteTamper = {}
    ids.forEach((id) => { if (tamper[id]) visible[id] = tamper[id] })
    return visible
  }, [siteIds, tamper])
}
